<script setup>
// AIVIO timers: «Журнал» — last firings of all timers of the account (newest first).
import { useI18n } from 'vue-i18n';

import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import Label from 'dashboard/components-next/label/Label.vue';
import { conversationRef, formatLogResult } from './timerUtils';

const props = defineProps({
  entries: { type: Array, default: () => [] },
  accountId: { type: [Number, String], required: true },
  inboxNames: { type: Object, default: () => ({}) },
});

const { t, locale } = useI18n();

const formatTime = value => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value || '';
  try {
    return date.toLocaleString(locale.value, {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return date.toLocaleString();
  }
};

const conversationPath = displayId =>
  frontendURL(conversationUrl({ accountId: props.accountId, id: displayId }));
</script>

<template>
  <p
    v-if="!entries.length"
    class="flex items-center justify-center py-20 text-base text-n-slate-12"
  >
    {{ t('AIVIO_TIMERS.LOG.EMPTY') }}
  </p>
  <div v-else class="overflow-x-auto">
    <table class="w-full text-sm text-start">
      <thead>
        <tr class="text-xs text-n-slate-11 border-b border-n-weak">
          <th class="py-2 pe-4 font-medium text-start whitespace-nowrap">
            {{ t('AIVIO_TIMERS.LOG.TIME') }}
          </th>
          <th class="py-2 pe-4 font-medium text-start">
            {{ t('AIVIO_TIMERS.LOG.RULE') }}
          </th>
          <th class="py-2 pe-4 font-medium text-start">
            {{ t('AIVIO_TIMERS.LOG.CONVERSATION') }}
          </th>
          <th class="py-2 pe-4 font-medium text-start">
            {{ t('AIVIO_TIMERS.LOG.MODE') }}
          </th>
          <th class="py-2 font-medium text-start">
            {{ t('AIVIO_TIMERS.LOG.RESULT') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(entry, index) in entries"
          :key="`${entry.t}-${entry.rule_id}-${entry.conversation_id}-${index}`"
          class="align-top border-b border-n-weak text-n-slate-12"
        >
          <td class="py-2 pe-4 whitespace-nowrap text-n-slate-11">
            {{ formatTime(entry.t) }}
          </td>
          <td class="py-2 pe-4">{{ entry.rule }}</td>
          <td class="py-2 pe-4 whitespace-nowrap">
            <router-link
              v-if="entry.display_id"
              :to="conversationPath(entry.display_id)"
              class="text-n-blue-11 hover:underline"
            >
              {{ conversationRef(entry.display_id) }}
            </router-link>
            <span
              v-if="inboxNames[entry.inbox_id]"
              class="block text-xs text-n-slate-11"
            >
              {{ inboxNames[entry.inbox_id] }}
            </span>
          </td>
          <td class="py-2 pe-4 whitespace-nowrap">
            <Label
              compact
              :color="entry.mode === 'live' ? 'teal' : 'slate'"
              :label="
                entry.mode === 'live'
                  ? t('AIVIO_TIMERS.MODES.LIVE')
                  : t('AIVIO_TIMERS.MODES.LOG')
              "
            />
          </td>
          <td class="py-2 text-n-slate-11">
            {{ formatLogResult(entry.result, t) }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
